/** @format */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox } from 'src/components';
import { ArrowDownIcon, CrossIcon } from '../icons';

const TopicSelectModal = ({
  open,
  onClose,
  topicTree = [],
  selectedTopicIds = [],
  onToggleTopic,
  loading = false,
  error = null,
  title: titleProp,
  confirmButtonLabel: confirmButtonLabelProp,
  loadingMessage: loadingMessageProp,
  tabs: tabsProp
}: {
  open?: boolean;
  onClose?: () => void;
  topicTree?: Array<{ id?: unknown; brand?: string; children?: unknown[]; title?: string; name?: string }>;
  selectedTopicIds?: unknown[];
  onToggleTopic?: (topicId: unknown) => void;
  loading?: boolean;
  error?: unknown;
  title?: string;
  confirmButtonLabel?: string;
  loadingMessage?: string;
  tabs?: Array<{ id: string; label: string }>;
}) => {
  const { t } = useTranslation();
  const title = titleProp ?? t('extranet.selectTopicsTitle');
  const confirmButtonLabel = confirmButtonLabelProp ?? t('common.button.ok');
  const loadingMessage = loadingMessageProp ?? t('extranet.loadingTopics');
  const tabs = useMemo(() => {
    if (Array.isArray(tabsProp) && tabsProp.length > 0) {
      return tabsProp;
    }
    return [
      { id: 'all', label: t('common.allTopics') },
      { id: 'ate', label: t('extranet.tabs.ate') },
      { id: 'vdo', label: t('extranet.tabs.vdo') }
    ];
  }, [tabsProp, t]);

  const [activeTab, setActiveTab] = useState('all');
  const [expandedNodes, setExpandedNodes] = useState({});

  useEffect(() => {
    if (!open) {
      setActiveTab('all');
      setExpandedNodes({});
    }
  }, [open]);

  const safeTopicTree = useMemo(() => (Array.isArray(topicTree) ? topicTree : []), [topicTree]);
  const safeSelectedIds = useMemo(() => (Array.isArray(selectedTopicIds) ? selectedTopicIds : []), [selectedTopicIds]);

  const filteredTopics = useMemo(() => {
    if (activeTab === 'all') {
      return safeTopicTree;
    }
    return safeTopicTree.filter(topic => (topic?.brand || '').toLowerCase() === activeTab);
  }, [activeTab, safeTopicTree]);

  const onToggleExpand = nodeKey => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeKey]: !prev[nodeKey]
    }));
  };

  const renderTopicNode = (topic, keyPath = []) => {
    const nodeKey = keyPath.join('-');
    const hasChildren = Array.isArray(topic?.children) && topic.children.length > 0;
    const isExpanded = Boolean(expandedNodes[nodeKey]);
    const isChecked = safeSelectedIds.includes(topic?.id);

    return (
      <li className="personal-data__newsletter-topic-item" key={nodeKey}>
        <div className="personal-data__newsletter-topic-row">
          <label className="personal-data__newsletter-topic-label">
            <Checkbox
              checked={isChecked}
              onChange={() => onToggleTopic(topic.id)}
              className="personal-data__checkbox"
              color="secondary"
            />
            <span>{topic?.displayName || topic?.name}</span>
          </label>
          {hasChildren ? (
            <button
              type="button"
              className="personal-data__newsletter-expand-btn"
              onClick={() => onToggleExpand(nodeKey)}
              aria-label={t('extranet.expandTopicAria')}>
              <span
                className={
                  isExpanded
                    ? 'personal-data__newsletter-chevron personal-data__newsletter-chevron--open'
                    : 'personal-data__newsletter-chevron'
                }>
                <ArrowDownIcon />
              </span>
            </button>
          ) : null}
        </div>
        {hasChildren && isExpanded ? (
          <ul className="personal-data__newsletter-topic-sublist">
            {topic.children.map((childTopic, childIndex) => renderTopicNode(childTopic, [...keyPath, childIndex]))}
          </ul>
        ) : null}
      </li>
    );
  };

  if (!open) {
    return null;
  }

  return (
    <div className="personal-data__newsletter-modal">
      <div className="personal-data__newsletter-modal-backdrop" onClick={onClose} />
      <div
        className="personal-data__newsletter-modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="topic-select-modal-title">
        <div className="personal-data__newsletter-modal-head">
          <h3 id="topic-select-modal-title">{title}</h3>
          <button type="button" className="personal-data__newsletter-modal-close" onClick={onClose}>
            <CrossIcon width={18} height={18} />
          </button>
        </div>

        <div className="personal-data__newsletter-modal-tabs">
          {tabs.map(tab => (
            <button
              className={`personal-data__newsletter-modal-tab ${
                activeTab === tab.id ? 'personal-data__newsletter-modal-tab--active' : ''
              }`}
              type="button"
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="personal-data__newsletter-modal-body">
          {loading ? <p>{loadingMessage}</p> : null}
          {error ? <p>{String(error)}</p> : null}
          {!loading && !error ? (
            <ul className="personal-data__newsletter-topic-list">
              {filteredTopics.map((topic, index) => renderTopicNode(topic, [index]))}
            </ul>
          ) : null}
        </div>

        <div className="personal-data__newsletter-modal-footer">
          <Button label={confirmButtonLabel} onClick={onClose} />
        </div>
      </div>
    </div>
  );
};

export default TopicSelectModal;
